import { ClerkProvider, useSession, useUser } from '@clerk/clerk-react'
import { LiveblocksProvider } from '@liveblocks/react/suspense'
import { NovuProvider } from '@novu/react'
import * as Sentry from '@sentry/react'
import { StableID, StatsigProvider, useStatsigClient } from '@statsig/react-bindings'
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics'
import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'
import {
  createClerkSupabaseClientCommandCenter,
  createClerkSupabaseClientCommandCenterEdgeExternal,
  decryptKey,
  fetchOrganizationUsersV3,
} from '../src/utility/utility.jsx'
import PaxafeLogo from './assets/Logos/Paxafe/logo.svg'
import PaxafeLogoShort from './assets/Logos/Paxafe/logoShort.svg'
import ContxtNewLogo from './assets/x-logo.svg'
import Loader from './components/Loader/Loader.jsx'
import RoutePermissionGate from './components/RoutePermissionGate/RoutePermissionGate.jsx'
import FileUploadQueueLegacyRedirect from './components/TemperatureDashboard/FileUploadQueue/FileUploadQueueLegacyRedirect'
import ExternalPagesGate from './components/Track/ExternalPagesGate.jsx'
import { UserPreferencesProvider } from './context/UserPreferencesContext'
import { useIdleSignOut } from './hooks/useIdleSignOut'
import {
  DASHBOARD_KEYS,
  getInsightsCommandCenterFallbackRoute,
  getStoredInsightsFeaturePermissions,
  isLanePerformanceInsightsAccessEnabled,
} from './pages/InsightsDashboard/lanePerformance/lanePerformanceConfig'
import SAMLRedirectGuard from './pages/SSO/SAMLRedirectGuard'
import SSOCallback from './pages/SSO/SSOCallback'
import {
  apiVersions,
  hydrateApiVersionMatrixFromClient,
  resolveEdgeFromApiVersions,
  useApiVersionMatrixFeatureGate,
} from './utility/apiVersionMatrix'
import { clearChunkReloadAttempts } from './utility/errorBoundary/chunkErrorRecovery'
import ErrorBoundary from './utility/ErrorBoundaryContainer.jsx'
import { isFileUploadQueueEnabled } from './utility/fileUploadQueueStatsigGateConstants'
import {
  getInsightsProductReleaseAliasPath,
  getInsightsProductReleasePath,
  getProductReleaseListAliasPath,
  getProductReleaseListPath,
  getProductReleaseSummaryAliasBasePath,
  getProductReleaseSummaryBasePath,
  getProductReleaseSummaryPath,
} from './utility/productReleasePaths'
import { buildRootCssBlock, setStyleTextContent } from './utility/safeDom'
import { clearShowAppAlertFlag } from './utility/showAppAlertStorage'
import {
  buildTenantDomainQueryOrFilters,
  resolveTenantLookupHostname,
  selectTenantByHostname,
} from './utility/tenantDomain'
import { getSafeWebsiteUrl } from './utility/url'
import './index.css'

const RETRYABLE_CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i,
  /Loading CSS chunk .* failed/i,
  /ChunkLoadError/i,
  /ERR_ABORTED/i,
]

function ProductReleaseListAliasRedirect() {
  const location = useLocation()
  const { client } = useStatsigClient()
  return (
    <Navigate
      to={`${getProductReleaseListPath(client)}${location.search}${location.hash}`}
      replace
    />
  )
}

function ProductReleaseNestedAliasRedirect() {
  const location = useLocation()
  const params = useParams()
  const { client } = useStatsigClient()
  const suffix = params['*'] ? `/${params['*']}` : ''
  return (
    <Navigate
      to={`${getProductReleaseListPath(client)}${suffix}${location.search}${location.hash}`}
      replace
    />
  )
}

function ProductReleaseSummaryAliasRedirect() {
  const { shipmentUuid, deviceId, deviceViewType } = useParams()
  const location = useLocation()
  const { client } = useStatsigClient()
  const target = getProductReleaseSummaryPath(client, shipmentUuid, deviceId, deviceViewType)
  return <Navigate to={`${target}${location.search}${location.hash}`} replace />
}

function InsightsProductReleaseAliasRedirect() {
  const { dashboardKey } = useParams()
  const location = useLocation()
  const { client } = useStatsigClient()
  const base = getInsightsProductReleasePath(client)
  const target = dashboardKey ? `${base}/${dashboardKey}` : base
  return <Navigate to={`${target}${location.search}${location.hash}`} replace />
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const isRetryableLazyError = (error) => {
  const message = String(error?.message || error)
  return RETRYABLE_CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message))
}

const lazyWithRetry = (importFn, moduleName, retries = 2, retryDelayMs = 600) =>
  lazy(async () => {
    let lastError
    for (let attempt = 0; attempt <= retries; attempt += 1) {
      try {
        return await importFn()
      } catch (error) {
        lastError = error
        if (!isRetryableLazyError(error) || attempt === retries) {
          throw error
        }
        const backoffDelay = retryDelayMs * (attempt + 1)
        console.warn(
          `[lazyWithRetry] ${moduleName} load failed, retrying in ${backoffDelay}ms`,
          error,
        )
        await sleep(backoffDelay)
      }
    }
    throw lastError
  })

const loadApp = () => import('./App.jsx')
const loadSignUpPage = () => import('./pages/SignUp/SignUp.jsx')
const loadSignOut = () => import('./components/SignOut/SignOut.jsx')
const loadHeader = () => import('./components/Header/Header.jsx')
const loadUserImpersonationEnhanced = () =>
  import('./components/Header/UserImpersonationEnhanced.jsx')
const loadProductProfile = () => import('./pages/ProductProfile/ProductProfile.jsx')
const loadOrganization = () => import('./components/Organization/Organization.jsx')
const loadInboxContainer = () => import('./pages/Inbox/InboxContainer.jsx')
const loadLaneProfile = () => import('./pages/LaneProfile/LaneProfile.jsx')
const loadShipments = () => import('./pages/Shipments/Shipments.jsx')
const loadShipmentDetails = () => import('./pages/ShipmentDetails/ShipmentDetails.jsx')
const loadShipmentShare = () => import('./pages/ShipmentShare/ShipmentShare.jsx')
const loadHelp = () => import('./pages/Help/Help.jsx')
const loadTagsV2 = () => import('./pages/Tags/TagsV2.jsx')
const loadAthenaShowroom = () => import('./pages/Athena/AthenaShowroom.jsx')
const loadQualificationShowroom = () => import('./pages/Qualification/QualificationShowroom.jsx')
const loadUserGroups = () => import('./pages/UserGroups/UserGroups.jsx')
const loadTempShipmentDetails = () =>
  import('./components/TemperatureDashboard/TempShipmentDetails.jsx')
const loadTempDashboardPrototype = () =>
  import('./pages/TemperatureDashboard/TempDashboardPrototype.jsx')
const loadNewShipment = () => import('./components/AddNewShipment/NewShipment.jsx')
const loadTempEditShipment = () => import('./components/TemperatureDashboard/TempEditShipment.jsx')
const loadAddTempData = () => import('./components/TemperatureDashboard/AddTempData.jsx')
const loadUploadTempData = () => import('./components/TemperatureDashboard/UploadTempData.jsx')
const loadMaintenance = () => import('./pages/Maintenance/Maintenance.jsx')
const loadModifyWorkflows = () => import('./pages/Workflows/ModifyWorkflows.jsx')
const loadWorkflowsList = () => import('./pages/Workflows/WorkflowsList.jsx')
const loadWorkflowHistoryPage = () => import('./pages/Workflows/WorkflowHistoryPage.jsx')
const loadLandingPage = () => import('./pages/LandingPage/LandingPage.jsx')
const loadInsightsDashboard = () => import('./pages/InsightsDashboard/InsightsDashboard.jsx')
const loadLiveShipment = () => import('./pages/LiveShipment/LiveShipment.jsx')
const loadTemperatureDashboard = () =>
  import('./pages/TemperatureDashboard/TemperatureDashboard.jsx')
const loadUploadShipmentDevice = () =>
  import('./components/UploadShipmentDevice/UploadShipmentDevice.jsx')
const loadFileUploadQueue = () =>
  import('./components/TemperatureDashboard/FileUploadQueue/FileUploadQueue.jsx')
const loadFileUploadQueueDetails = () =>
  import('./components/TemperatureDashboard/FileUploadQueue/FileDetails.jsx')
const loadTenantsList = () => import('./pages/Tenants/TenantsListPage.jsx')
const loadTenantOnboarding = () => import('./pages/Tenants/TenantOnboardingPage.jsx')
const loadTenantOrganizations = () => import('./pages/Tenants/TenantOrganizationsPage.jsx')
const loadTenantOrgCreate = () => import('./pages/Tenants/TenantOrgCreatePage.jsx')
const loadTenantOrgDetail = () => import('./pages/Tenants/TenantOrgDetailPage.jsx')
const loadApiVersions = () => import('./pages/ApiVersions/ApiVersions.jsx')
const loadAdminSettingsOrganizations = () =>
  import('./pages/AdminSettings/OrganizationManagement.jsx')
const loadAdminSettingsFeatures = () => import('./pages/AdminSettings/FeatureManagement.jsx')
const loadOrganizationMangement = () => import('./pages/Organization/OrganizationManagement.jsx')
const loadUserManagement = () => import('./pages/Organization/UserManagement.jsx')
const loadTenantOrgListPage = () => import('./pages/Tenant/TenantOrgList.jsx')
const loadTenantOrgManagementPage = () => import('./pages/Tenant/TenantOrgManagement.jsx')
const loadTrackLookup = () => import('./pages/Track/TrackLookup.jsx')
const loadTrackShipmentSummary = () => import('./pages/Track/TrackShipmentSummary.jsx')

const App = lazyWithRetry(loadApp, 'App')
const SignUpPage = lazyWithRetry(loadSignUpPage, 'SignUpPage')
const SignOut = lazyWithRetry(loadSignOut, 'SignOut')
const Header = lazyWithRetry(loadHeader, 'Header')
const UserImpersonationEnhanced = lazyWithRetry(
  loadUserImpersonationEnhanced,
  'UserImpersonationEnhanced',
)
const ProductProfile = lazyWithRetry(loadProductProfile, 'ProductProfile')
const Organization = lazyWithRetry(loadOrganization, 'Organization')
const InboxContainer = lazyWithRetry(loadInboxContainer, 'InboxContainer')
const LaneProfile = lazyWithRetry(loadLaneProfile, 'LaneProfile')
const Shipments = lazyWithRetry(loadShipments, 'Shipments')
const ShipmentDetails = lazyWithRetry(loadShipmentDetails, 'ShipmentDetails')
const ShipmentShare = lazyWithRetry(loadShipmentShare, 'ShipmentShare')
const Help = lazyWithRetry(loadHelp, 'Help')
const TagsV2 = lazyWithRetry(loadTagsV2, 'TagsV2')

const AthenaShowroom = lazyWithRetry(loadAthenaShowroom, 'AthenaShowroom')
const QualificationShowroom = lazyWithRetry(loadQualificationShowroom, 'QualificationShowroom')
const UserGroups = lazyWithRetry(loadUserGroups, 'UserGroups')
const TempShipmentDetails = lazyWithRetry(loadTempShipmentDetails, 'TempShipmentDetails')
const TempDashboardPrototype = lazyWithRetry(loadTempDashboardPrototype, 'TempDashboardPrototype')
const NewShipment = lazyWithRetry(loadNewShipment, 'NewShipment')
const TempEditShipment = lazyWithRetry(loadTempEditShipment, 'TempEditShipment')
const AddTempData = lazyWithRetry(loadAddTempData, 'AddTempData')
const UploadTempData = lazyWithRetry(loadUploadTempData, 'UploadTempData')
const Maintenance = lazyWithRetry(loadMaintenance, 'Maintenance')
const ModifyWorkflows = lazyWithRetry(loadModifyWorkflows, 'ModifyWorkflows')
const WorkflowsList = lazyWithRetry(loadWorkflowsList, 'WorkflowsList')
const WorkflowHistoryPage = lazyWithRetry(loadWorkflowHistoryPage, 'WorkflowHistoryPage')
const LandingPage = lazyWithRetry(loadLandingPage, 'LandingPage')
const InsightsDashboard = lazyWithRetry(loadInsightsDashboard, 'InsightsDashboard')
const LiveShipment = lazyWithRetry(loadLiveShipment, 'LiveShipment')
const TemperatureDashboard = lazyWithRetry(loadTemperatureDashboard, 'TemperatureDashboard')
const UploadShipmentDevice = lazyWithRetry(loadUploadShipmentDevice, 'UploadShipmentDevice')
const FileUploadQueue = lazyWithRetry(loadFileUploadQueue, 'FileUploadQueue')
const FileUploadQueueDetails = lazyWithRetry(loadFileUploadQueueDetails, 'FileUploadQueueDetails')
const TenantsListPage = lazyWithRetry(loadTenantsList, 'TenantsListPage')
const TenantOnboardingPage = lazyWithRetry(loadTenantOnboarding, 'TenantOnboardingPage')
const TenantOrganizationsPage = lazyWithRetry(loadTenantOrganizations, 'TenantOrganizationsPage')
const TenantOrgCreatePage = lazyWithRetry(loadTenantOrgCreate, 'TenantOrgCreatePage')
const TenantOrgDetailPage = lazyWithRetry(loadTenantOrgDetail, 'TenantOrgDetailPage')
const ApiVersions = lazyWithRetry(loadApiVersions, 'ApiVersions')
const AdminSettingsOrganizationManagement = lazyWithRetry(
  loadAdminSettingsOrganizations,
  'AdminSettingsOrganizationManagement',
)
const AdminSettingsFeatureManagement = lazyWithRetry(
  loadAdminSettingsFeatures,
  'AdminSettingsFeatureManagement',
)
const OrganizationManagement = lazyWithRetry(loadOrganizationMangement, 'OrganizationManagement')
const UserManagement = lazyWithRetry(loadUserManagement, 'UserManagement')
const TenantOrgManagement = lazyWithRetry(loadTenantOrgManagementPage, 'TenantOrgManagementPage')
const TenantOrgListPage = lazyWithRetry(loadTenantOrgListPage, 'TenantOrgListPage')
const TrackLookup = lazyWithRetry(loadTrackLookup, 'TrackLookup')
const TrackShipmentSummary = lazyWithRetry(loadTrackShipmentSummary, 'TrackShipmentSummary')

const preloadAuthenticatedRoutes = async () => {
  const preloaders = [
    loadLiveShipment,
    loadTemperatureDashboard,
    loadTempShipmentDetails,
    loadShipmentDetails,
    loadShipments,
    loadWorkflowsList,
    loadModifyWorkflows,
    loadProductProfile,
    loadLaneProfile,
    loadInboxContainer,
    loadTagsV2,
    loadHeader,
    loadOrganization,
    loadUserGroups,
    loadInsightsDashboard,
  ]

  await Promise.allSettled(preloaders.map((preload) => preload()))
}

const STATSIG_SDK_KEY = import.meta.env.VITE_STATSIG_CLIENT_SDK_KEY
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT
const NOVU_APPLICATION_IDENTIFIER = import.meta.env.VITE_NOVU_APPLICATION_IDENTIFIER

// Suppress console.log statements in production
if (ENVIRONMENT === 'production') {
  window.console.log = () => {}
}

let usersList = []

/* INITIALIZE SENTRY CONFIG */
if (ENVIRONMENT === 'production' || ENVIRONMENT === 'staging') {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    sendDefaultPii: false,
    beforeSend(event) {
      if (event.user) {
        // Don't send user's email address
        delete event.user.email
        delete event.user.email_address
        delete event.user.primaryEmailAddress
        delete event.user.phoneNumber
        delete event.user.phone_number
        delete event.user.phone
      }
      return event
    },
  })
}

function RootApp() {
  const isReadyRef = useRef(false)
  const cc_client_edge = createClerkSupabaseClientCommandCenterEdgeExternal()

  const [publishableKey, setPublishableKey] = useState(null)
  const [tenantName, setTenantName] = useState(null)
  const [localization, setLocalization] = useState(null)
  const loadWhiteLabelConfigRef = useRef(null)

  function loadTenantName(name) {
    if (name === 'Paxafe') {
      localStorage.tenantName = 'CONTXT'
      document.title = 'CONTXT - PAXAFE'
      setTenantName('CONTXT')
    } else {
      localStorage.tenantName = name || 'CONTXT'
      document.title = name || 'CONTXT - PAXAFE'
      setTenantName(name || 'CONTXT')
    }
  }

  function loadTheme(domainConfig) {
    const themeStyle = buildRootCssBlock(domainConfig?.design_tokens, domainConfig?.font_family)
    const style = document.createElement('style')
    setStyleTextContent(style, themeStyle)
    document.head.appendChild(style)
  }

  function loadLightdashPaletteUuid(paletteUuid) {
    if (paletteUuid) {
      localStorage.lightdashPaletteUuid = paletteUuid
    } else {
      delete localStorage.lightdashPaletteUuid
    }
  }

  function loadFontFamily(fontFamily) {
    const fontUrl = `https://fonts.googleapis.com/css2?family=${fontFamily}&display=swap`

    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = fontUrl
    document.head.appendChild(link)
  }

  function loadLogos(domainConfig, poweredByPaxafe, poweredByPaxafeSize) {
    localStorage.poweredByPaxafe = poweredByPaxafe
    localStorage.poweredByPaxafeSize = poweredByPaxafeSize
    localStorage.logoPng = domainConfig?.logo_png || PaxafeLogoShort
    localStorage.longLogo = domainConfig?.long_logo || PaxafeLogo
    localStorage.shortLogo = domainConfig?.short_logo || PaxafeLogoShort
    if (localStorage.shortLogo) {
      const faviconLink = document.querySelector('link[rel="shortcut icon"]')
      const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]')
      if (faviconLink) {
        faviconLink.href = localStorage.shortLogo
      }
      if (appleTouchIcon) {
        appleTouchIcon.href = localStorage.shortLogo
      }
    }
  }

  function loadFeatures(featureConfig) {
    localStorage.features = JSON.stringify(featureConfig)
  }

  function loadWebsiteUrl(websiteUrl) {
    localStorage.websiteUrl = getSafeWebsiteUrl(websiteUrl)
  }

  async function loadWhiteLabelConfig(hostname) {
    try {
      const lookupHostname = resolveTenantLookupHostname(hostname, {
        e2eEnvironment: import.meta.env.VITE_E2E_ENVIRONMENT,
        tenantDomainOverride: import.meta.env.VITE_E2E_CI_TENANT_DOMAIN,
      })

      const tenantSelect =
        'tenant_name, ui_config, feature_config, powered_by_paxafe, powered_by_paxafe_size, tenant_id, novu_config, lightdash_palette_id, website_url, domain, connection_id'

      const { data: tenantCandidates, error } = await cc_client_edge
        .from('tenants')
        .select(tenantSelect)
        .or(buildTenantDomainQueryOrFilters(lookupHostname))

      if (error) {
        console.error('Error fetching tenant details:', error)
        return
      }

      const data = selectTenantByHostname(lookupHostname, tenantCandidates)
      if (!data) {
        console.error('Error fetching tenant details: no tenant matched hostname', lookupHostname)
        return
      }

      await hydrateApiVersionMatrixFromClient(cc_client_edge)
      const clerkSecretApiUrl =
        apiVersions['get-encrypted-clerk-secret'] || 'get-encrypted-clerk-secret'
      const { data: encryptedClerkPublishableKeyData, error: clerkSecretError } =
        await cc_client_edge.functions.invoke(resolveEdgeFromApiVersions(clerkSecretApiUrl), {
          body: { tenantId: data?.tenant_id },
        })
      const clerkPublishableKey = await decryptKey(
        encryptedClerkPublishableKeyData?.data,
        encryptedClerkPublishableKeyData?.iv,
      )

      if (clerkSecretError) {
        console.error('Error fetching clerk secret:', clerkSecretError)
      }
      if (window?.localStorage) {
        window.localStorage.setItem('tenantId', data?.tenant_id || '')
        if (data?.connection_id) {
          window.localStorage.setItem('connection_id', JSON.stringify(data?.connection_id || {}))
        }
      }
      loadTenantName(data?.tenant_name)
      loadTheme(data?.ui_config)
      loadFontFamily(data?.ui_config?.font_family)
      loadLogos(data?.ui_config, data?.powered_by_paxafe, data?.powered_by_paxafe_size)
      loadFeatures(data?.feature_config)
      loadWebsiteUrl(data?.website_url)
      loadLightdashPaletteUuid(data?.lightdash_palette_id)
      setPublishableKey(clerkPublishableKey)
      setLocalization({
        signIn: {
          start: {
            title: `Sign in to ${data?.tenant_name ?? 'App'}`,
            titleCombined: `Sign in to ${data?.tenant_name ?? 'App'}`,
            subtitle: 'Welcome back! Please sign in to continue',
            subtitleCombined: 'Welcome back! Please sign in to continue',
          },
        },
      })
    } catch (error) {
      console.error('Error fetching tenant details:', error)
    }
  }

  loadWhiteLabelConfigRef.current = loadWhiteLabelConfig

  // On full page refresh, start with clean unsaved-navigation session state.
  useEffect(() => {
    clearShowAppAlertFlag()
    clearChunkReloadAttempts()
  }, [])

  useEffect(() => {}, [publishableKey])

  useEffect(() => {
    if (!isReadyRef.current) {
      isReadyRef.current = true
      void loadWhiteLabelConfigRef.current?.(window.location.hostname)
    }
  }, [])

  if (!publishableKey) {
    return <Loader />
  }

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      localization={localization}
      signInUrl="/"
      signUpUrl="/sign-up"
      fallbackRedirectUrl="/"
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
      signInForceRedirectUrl="/"
      signUpForceRedirectUrl="/"
      telemetry={false}
    >
      <CommandCenterApp tenantName={tenantName} />
    </ClerkProvider>
  )
}

function CommandCenterApp({ tenantName }) {
  const hasPreloadedRoutesRef = useRef(false)
  const userListFetchInFlightRef = useRef(false)
  const { session } = useSession()

  const { user, isLoaded } = useUser()
  const [userList, setUserList] = useState(usersList)

  const cc_client = createClerkSupabaseClientCommandCenter(session)

  const orgId = window?.Clerk?.organization?.id || ''

  useEffect(() => {
    if (!cc_client || !isLoaded || !user?.id) {
      return
    }

    hydrateApiVersionMatrixFromClient(cc_client).catch((error) => {
      console.error('Failed to hydrate api_version_matrix:', error)
    })
  }, [cc_client, isLoaded, user?.id])

  useEffect(() => {
    if (!orgId || userList.length > 0) {
      return
    }
    if (userListFetchInFlightRef.current) {
      return
    }

    async function getUserList(organizationId) {
      const response = await fetchOrganizationUsersV3(session, { organization_id: organizationId })
      if (response?.success) {
        setUserList(response?.data?.data)
        usersList = response?.data?.data
      }
    }

    userListFetchInFlightRef.current = true
    void getUserList(orgId).finally(() => {
      userListFetchInFlightRef.current = false
    })
  }, [orgId, userList.length, session])

  useEffect(() => {
    if (!isLoaded || !user?.id || hasPreloadedRoutesRef.current) {
      return
    }

    hasPreloadedRoutesRef.current = true
    const preloadWhenIdle = () => {
      preloadAuthenticatedRoutes().catch((error) => {
        console.warn('[route-preload] Background route preload failed', error)
      })
    }

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(preloadWhenIdle, { timeout: 3000 })
      return
    }

    const timeoutId = setTimeout(preloadWhenIdle, 800)
    return () => clearTimeout(timeoutId)
  }, [isLoaded, user?.id])

  if (!isLoaded) {
    return <Loader />
  }

  const resolveUsers = async ({ userIds }) => {
    return userIds.map((userId) => {
      // Check if user is System
      if (
        userId &&
        (userId.toLowerCase().includes('system') ||
          userId === 'system' ||
          userId.startsWith('System'))
      ) {
        return {
          name: 'System',
          avatar: ContxtNewLogo,
        }
      }

      if (userId === user?.id && user) {
        return {
          name:
            user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Anonymous',
          avatar: user.imageUrl || '',
        }
      }

      const foundUser = userList.find(
        (userMember) => userMember.public_user_data?.user_id === userId,
      )
      if (foundUser && foundUser.public_user_data) {
        const userData = foundUser.public_user_data
        return {
          name:
            `${userData.first_name || ''} ${userData.last_name || ''}`.trim() ||
            userData.identifier ||
            'Unknown User',
          avatar: userData.image_url || userData.profile_image_url || '',
        }
      }

      // Check if user is automation user
      if (userId && !userId.startsWith('user_')) {
        return {
          name: userId,
          avatar: ContxtNewLogo,
        }
      }

      return {
        name: 'Unknown User',
        avatar: '',
      }
    })
  }

  const resolveMentionSuggestions = async ({ text }) => {
    const searchText = text?.toLowerCase() || ''
    const suggestions = []

    if (searchText.length < 2) {
      return suggestions
    }

    if (user?.id) {
      const currentUserName = user?.fullName || `${user?.firstName} ${user?.lastName}` || ''
      const currentUserEmail = user?.primaryEmailAddress?.emailAddress || ''

      if (
        currentUserName.toLowerCase().includes(searchText) ||
        currentUserEmail.toLowerCase().includes(searchText)
      ) {
        suggestions.push(user.id)
      }
    }

    userList?.forEach((userMember) => {
      const userData = userMember.public_user_data
      if (!userData?.user_id) return

      if (userData.user_id === user?.id) return

      const fullName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim()
      const identifier = userData.identifier || ''

      if (
        fullName.toLowerCase().includes(searchText) ||
        userData.first_name?.toLowerCase().includes(searchText) ||
        userData.last_name?.toLowerCase().includes(searchText) ||
        identifier.toLowerCase().includes(searchText)
      ) {
        suggestions.push(userData.user_id)
      }
    })

    return suggestions
  }

  const authenticateWithLiveblocks = async (room) => {
    try {
      const { data, error } = await cc_client.functions.invoke(
        resolveEdgeFromApiVersions('liveblocks-auth-v1'),
        {
          body: {
            roomId: room,
            userId: user?.id,
            userInfo: {
              name:
                user.fullName ||
                `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
                'Anonymous',
              avatar: user.imageUrl || '',
              email: user.primaryEmailAddress?.emailAddress || '',
            },
          },
        },
      )

      if (error) {
        console.error('Liveblocks authentication error:', error)
        throw new Error(`Liveblocks authentication failed: ${error.message || error}`)
      }

      console.warn('Successfully authenticated with Liveblocks for user:', user?.id)

      return {
        token: data.token,
      }
    } catch (error) {
      console.error('Error in authEndpoint:', error)
      throw error
    }
  }

  // Conditionally build Statsig options based on environment
  const statsigOptions = {
    environment: { tier: ENVIRONMENT },
    ...(ENVIRONMENT === 'staging' && { plugins: [new StatsigAutoCapturePlugin()] }),
    fetchMode: 'network-only',
  }

  // Gates using ID type "stableID" need this on the user; the SDK does not attach it for evaluations.
  const statsigStableId = StableID.get(STATSIG_SDK_KEY ?? '')

  return (
    <StatsigProvider
      sdkKey={STATSIG_SDK_KEY}
      options={statsigOptions}
      user={{
        userID: user?.id || 'anonymous',
        customIDs: {
          ...(statsigStableId ? { stableID: statsigStableId } : {}),
          orgID: window?.Clerk?.organization?.id || 'test-org',
          tenantName: tenantName || 'CONTXT',
        },
        custom: {
          orgID: window?.Clerk?.organization?.id || 'test-org',
          tenantName: tenantName || 'CONTXT',
        },
      }}
      loadingComponent={<Loader />}
    >
      <ApiVersionMatrixGateSync>
        <UserPreferencesProvider>
          {user?.id ? (
            <NovuProvider
              applicationIdentifier={NOVU_APPLICATION_IDENTIFIER}
              subscriberId={user?.id}
              context={{ org: orgId }}
            >
              <LiveblocksProvider
                resolveUsers={resolveUsers}
                authEndpoint={authenticateWithLiveblocks}
                resolveMentionSuggestions={resolveMentionSuggestions}
              >
                <AppRoutes orgId={orgId} />
              </LiveblocksProvider>
            </NovuProvider>
          ) : (
            <BrowserRouter>
              <ErrorBoundary>
                <Suspense fallback={<Loader />}>
                  <Routes>
                    <Route path="/external/:pageType/:view/*" element={<ShipmentShare />} />
                    <Route
                      path="/track"
                      element={
                        <ExternalPagesGate>
                          <TrackLookup />
                        </ExternalPagesGate>
                      }
                    />
                    <Route
                      path="/track/shipment/:encryptedCode"
                      element={
                        <ExternalPagesGate>
                          <TrackShipmentSummary />
                        </ExternalPagesGate>
                      }
                    />
                    <Route path="/sso-callback" element={<SSOCallback />} />
                    <Route path="*" element={<App />} />
                    <Route path="/maintenance" element={<Maintenance />} />
                    <Route path="/" element={<App />} />
                    <Route path="/login" element={<App />} />
                    <Route path="/sign-up" element={<SignUpPage />} />
                  </Routes>
                </Suspense>
              </ErrorBoundary>
            </BrowserRouter>
          )}
        </UserPreferencesProvider>
      </ApiVersionMatrixGateSync>
    </StatsigProvider>
  )
}

function ApiVersionMatrixGateSync({ children }) {
  useApiVersionMatrixFeatureGate()

  return children
}

function AppRoutes({ orgId }) {
  const { client } = useStatsigClient()
  useIdleSignOut()
  const maintenanceConfig = client.getDynamicConfig('maintenance-mode')
  const isMaintenanceEnabledOrgs = maintenanceConfig.get('exempt-maintenance-orgIds', [])

  const isMaintenanceEnabled = isMaintenanceEnabledOrgs.includes(orgId)
  const ROUTE_PERMISSION_KEYS = RoutePermissionGate.PERMISSION_KEYS

  const perm = (permission, element, withHeaderShell = false, { pageNotAccessibleCheck } = {}) => (
    <RoutePermissionGate
      permission={permission}
      withHeaderShell={withHeaderShell}
      pageNotAccessibleCheck={pageNotAccessibleCheck}
    >
      {element}
    </RoutePermissionGate>
  )
  const pageNotAccessibleRoute = { pageNotAccessibleCheck: true }

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <Suspense fallback={<Loader />}>
          <SAMLRedirectGuard>
            <Routes>
              {!isMaintenanceEnabled ? (
                <>
                  <Route path="/landing-page" element={<LandingPage />} />
                  <Route
                    path="/shipment/commandcenter"
                    element={perm(ROUTE_PERMISSION_KEYS.COMMAND_CENTER, <LiveShipment />, true)}
                  />
                  <Route
                    path="/shipment/live"
                    element={perm(ROUTE_PERMISSION_KEYS.COMMAND_CENTER, <LiveShipment />, true)}
                  />
                  <Route
                    path="/shipment/completed"
                    element={perm(ROUTE_PERMISSION_KEYS.COMMAND_CENTER, <LiveShipment />, true)}
                  />
                  <Route
                    path={getProductReleaseListPath(client)}
                    element={perm(
                      ROUTE_PERMISSION_KEYS.TEMP_RELEASE,
                      <TemperatureDashboard />,
                      true,
                    )}
                  />
                  <Route
                    path={getProductReleaseListAliasPath(client)}
                    element={<ProductReleaseListAliasRedirect />}
                  />
                  <Route
                    path={`${getProductReleaseListPath(client)}/create`}
                    element={perm(ROUTE_PERMISSION_KEYS.TEMP_RELEASE_MANAGE, <NewShipment />, true)}
                  />
                  <Route
                    path={`${getProductReleaseListPath(client)}/:shipmentUuid/upload`}
                    element={perm(
                      ROUTE_PERMISSION_KEYS.TEMP_RELEASE,
                      <AddTempData />,
                      true,
                      pageNotAccessibleRoute,
                    )}
                  />
                  <Route
                    path={`${getProductReleaseListPath(client)}/:shipmentUuid/upload/:uploadType`}
                    element={perm(
                      ROUTE_PERMISSION_KEYS.TEMP_RELEASE,
                      <UploadTempData />,
                      true,
                      pageNotAccessibleRoute,
                    )}
                  />
                  <Route
                    path="/shipment/:view/:shipmentUuid/upload-v2"
                    element={perm(
                      [ROUTE_PERMISSION_KEYS.COMMAND_CENTER, ROUTE_PERMISSION_KEYS.TEMP_RELEASE],
                      <UploadShipmentDevice />,
                      true,
                      pageNotAccessibleRoute,
                    )}
                  />
                  <Route
                    path="/shipment/:view/:shipmentUuid/file-upload-queue"
                    element={perm(
                      [ROUTE_PERMISSION_KEYS.COMMAND_CENTER, ROUTE_PERMISSION_KEYS.TEMP_RELEASE],
                      isFileUploadQueueEnabled(client) ? (
                        <FileUploadQueue />
                      ) : (
                        <FileUploadQueueLegacyRedirect />
                      ),
                      true,
                      pageNotAccessibleRoute,
                    )}
                  />
                  <Route
                    path="/shipment/:view/:shipmentUuid/file-upload-queue/:documentId"
                    element={perm(
                      [ROUTE_PERMISSION_KEYS.COMMAND_CENTER, ROUTE_PERMISSION_KEYS.TEMP_RELEASE],
                      isFileUploadQueueEnabled(client) ? (
                        <FileUploadQueueDetails />
                      ) : (
                        <FileUploadQueueLegacyRedirect />
                      ),
                      true,
                      pageNotAccessibleRoute,
                    )}
                  />
                  <Route
                    path={`${getProductReleaseListPath(client)}/edit/:shipmentUuid`}
                    element={perm(
                      ROUTE_PERMISSION_KEYS.TEMP_RELEASE_MANAGE,
                      <TempEditShipment />,
                      true,
                    )}
                  />
                  <Route
                    path={`${getProductReleaseListPath(client)}/details`}
                    element={perm(
                      ROUTE_PERMISSION_KEYS.TEMP_RELEASE,
                      <TempShipmentDetails />,
                      true,
                    )}
                  />
                  <Route
                    path={`${getProductReleaseListAliasPath(client)}/*`}
                    element={<ProductReleaseNestedAliasRedirect />}
                  />
                  <Route
                    path="/shipment/:view/:shipmentId/:eventId?/:eventTypeParam?/:deviceIdParam?"
                    element={perm(ROUTE_PERMISSION_KEYS.COMMAND_CENTER, <ShipmentDetails />, true)}
                  />
                  <Route path="/temp-release-demo" element={<TempDashboardPrototype />} />
                  <Route
                    path="automation/workflows"
                    element={perm(
                      ROUTE_PERMISSION_KEYS.AUTOMATION_WORKFLOWS,
                      <WorkflowsList />,
                      true,
                    )}
                  />
                  <Route
                    path="automation/workflows/:workflowId/history"
                    element={perm(
                      ROUTE_PERMISSION_KEYS.AUTOMATION_WORKFLOWS,
                      <WorkflowHistoryPage />,
                      true,
                    )}
                  />
                  <Route
                    path="automation/workflows/:workflowId"
                    element={perm(
                      ROUTE_PERMISSION_KEYS.AUTOMATION_WORKFLOWS,
                      <ModifyWorkflows />,
                      true,
                    )}
                  />
                  <Route
                    path="/automation/workflows/create"
                    element={perm(
                      ROUTE_PERMISSION_KEYS.AUTOMATION_WORKFLOWS_MANAGE,
                      <ModifyWorkflows />,
                      true,
                    )}
                  />
                  <Route
                    path={`${getProductReleaseSummaryBasePath(client)}/:shipmentUuid`}
                    element={perm(
                      ROUTE_PERMISSION_KEYS.TEMP_RELEASE,
                      <TempShipmentDetails />,
                      true,
                    )}
                  />
                  <Route
                    path={`${getProductReleaseSummaryBasePath(client)}/:shipmentUuid/:deviceId?/:deviceViewType?`}
                    element={perm(
                      ROUTE_PERMISSION_KEYS.TEMP_RELEASE,
                      <TempShipmentDetails />,
                      true,
                    )}
                  />
                  <Route
                    path={`${getProductReleaseSummaryAliasBasePath(client)}/:shipmentUuid/:deviceId?/:deviceViewType?`}
                    element={<ProductReleaseSummaryAliasRedirect />}
                  />
                  <Route path="/external/:pageType/:view/*" element={<ShipmentShare />} />
                  <Route
                    path="/track"
                    element={
                      <ExternalPagesGate>
                        <TrackLookup />
                      </ExternalPagesGate>
                    }
                  />
                  <Route
                    path="/track/shipment/:encryptedCode"
                    element={
                      <ExternalPagesGate>
                        <TrackShipmentSummary />
                      </ExternalPagesGate>
                    }
                  />
                  <Route
                    path="/shipment/create"
                    element={perm(ROUTE_PERMISSION_KEYS.COMMAND_CENTER_MANAGE, <Shipments />, true)}
                  />
                  <Route
                    path="/product-profiles/:productId?"
                    element={perm(ROUTE_PERMISSION_KEYS.PRODUCT_PROFILES, <ProductProfile />, true)}
                  />
                  <Route
                    path="/tags"
                    element={perm(ROUTE_PERMISSION_KEYS.TAGS, <TagsV2 />, true)}
                  />
                  <Route
                    path="/lane-profiles/:laneProfileId?"
                    element={perm(ROUTE_PERMISSION_KEYS.LANE_PROFILES, <LaneProfile />, true)}
                  />
                  <Route
                    path="/user-access-groups/:userAccessGroupId?"
                    element={perm(ROUTE_PERMISSION_KEYS.LEGACY_MANAGE, <UserGroups />, true)}
                  />
                  <Route
                    path="/tenants/:tenantId/organizations/create"
                    element={perm(
                      ROUTE_PERMISSION_KEYS.TENANT_ONBOARDING,
                      <TenantOrgCreatePage />,
                      true,
                    )}
                  />
                  <Route
                    path="/tenants/:tenantId/organizations/:organizationId"
                    element={perm(
                      ROUTE_PERMISSION_KEYS.TENANT_ONBOARDING,
                      <TenantOrgDetailPage />,
                      true,
                    )}
                  />
                  <Route
                    path="/tenants/:tenantId/organizations"
                    element={perm(
                      ROUTE_PERMISSION_KEYS.TENANT_ONBOARDING,
                      <TenantOrganizationsPage />,
                      true,
                    )}
                  />
                  <Route
                    path="/tenants/:tenantId"
                    element={perm(
                      ROUTE_PERMISSION_KEYS.TENANT_ONBOARDING,
                      <TenantOnboardingPage />,
                      true,
                    )}
                  />
                  <Route
                    path="/tenants"
                    element={perm(
                      ROUTE_PERMISSION_KEYS.TENANT_ONBOARDING,
                      <TenantsListPage />,
                      true,
                    )}
                  />
                  <Route
                    path="/admin-settings/organizations"
                    element={perm(
                      ROUTE_PERMISSION_KEYS.ADMIN_ORG_SETTINGS,
                      <AdminSettingsOrganizationManagement />,
                      true,
                    )}
                  />
                  <Route
                    path="/admin-settings/features"
                    element={perm(
                      ROUTE_PERMISSION_KEYS.ADMIN_FEATURE_MANAGEMENT,
                      <AdminSettingsFeatureManagement />,
                      true,
                    )}
                  />
                  <Route
                    path="/organization/:orgId/:manage/:manageId?"
                    element={perm(
                      ROUTE_PERMISSION_KEYS.ORG_OWNER_SETTINGS,
                      <OrganizationManagement />,
                      true,
                    )}
                  />
                  <Route
                    path="/user/:userId/:manage"
                    element={perm(
                      ROUTE_PERMISSION_KEYS.UAG_USER_SETTINGS,
                      <UserManagement />,
                      true,
                    )}
                  />
                  <Route
                    path="/user-impersonation"
                    element={perm(
                      ROUTE_PERMISSION_KEYS.USER_IMPERSONATION,
                      <UserImpersonationEnhanced />,
                      true,
                    )}
                  />
                  <Route
                    path="/tenant/org-management/:orgId/:orgViewType"
                    element={perm(
                      ROUTE_PERMISSION_KEYS.TENANT_ORG_MANAGEMENT,
                      <TenantOrgManagement />,
                      true,
                    )}
                  />
                  <Route
                    path="*"
                    element={
                      <Header>
                        <Routes>
                          <Route
                            path="/manage/:tab?"
                            element={perm('manage_dynamic', <Organization />)}
                          />
                          <Route path="/inbox" element={<InboxContainer />} />
                          <Route
                            path="/help"
                            element={perm(ROUTE_PERMISSION_KEYS.HELP, <Help />)}
                          />
                          <Route
                            path="/athena-sandbox"
                            element={perm(ROUTE_PERMISSION_KEYS.ATHENA_SANDBOX, <AthenaShowroom />)}
                          />
                          <Route
                            path="/qualification-sandbox"
                            element={perm(
                              ROUTE_PERMISSION_KEYS.QUALIFICATION_SANDBOX,
                              <QualificationShowroom />,
                            )}
                          />
                          <Route
                            path="/insights/lane-performance"
                            element={perm(
                              ROUTE_PERMISSION_KEYS.INSIGHTS_LANE_PERFORMANCE,
                              <Navigate
                                to={`/insights/lane-performance/${DASHBOARD_KEYS.EXECUTIVE_SUMMARY}`}
                                replace
                              />,
                            )}
                          />
                          <Route
                            path="/insights/lane-performance-feedback"
                            element={perm(
                              ROUTE_PERMISSION_KEYS.INSIGHTS_LANE_PERFORMANCE_FEEDBACK,
                              isLanePerformanceInsightsAccessEnabled(
                                getStoredInsightsFeaturePermissions(),
                              ) ? (
                                <Navigate
                                  to="/insights/lane-performance/executiveSummary"
                                  replace
                                />
                              ) : (
                                <Navigate
                                  to={getInsightsCommandCenterFallbackRoute(client)}
                                  replace
                                />
                              ),
                            )}
                          />
                          <Route
                            path={`${getInsightsProductReleaseAliasPath(client)}/:dashboardKey?`}
                            element={<InsightsProductReleaseAliasRedirect />}
                          />
                          <Route
                            path="/insights/:insightsType/:dashboardKey?"
                            element={perm('insights_dynamic', <InsightsDashboard />)}
                          />
                          <Route
                            path="api-versions"
                            element={perm(ROUTE_PERMISSION_KEYS.API_CONFIG, <ApiVersions />)}
                          />
                          <Route
                            path="/tenant/org-management"
                            element={perm(
                              ROUTE_PERMISSION_KEYS.TENANT_ORG_MANAGEMENT,
                              <TenantOrgListPage />,
                            )}
                          />
                        </Routes>
                      </Header>
                    }
                  />
                  <Route path="/sign-out" element={<SignOut />} />
                  <Route path="/sign-up" element={<SignUpPage />} />
                  <Route path="/" element={<App />} />
                  <Route path="/login" element={<App />} />
                  {/* Clerk's SSO callback — finalises the session post-IdP-redirect. */}
                  <Route path="/sso-callback" element={<SSOCallback />} />
                </>
              ) : (
                <>
                  <Route path="/sso-callback" element={<SSOCallback />} />
                  <Route path="/maintenance" element={<Maintenance />} />
                  <Route path="*" element={<Maintenance />} />
                  <Route path="/login" element={<App />} />
                  <Route path="/sign-up" element={<SignUpPage />} />
                  <Route path="/" element={<App />} />
                </>
              )}
            </Routes>
          </SAMLRedirectGuard>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  )
}

ReactDOM.createRoot(document.getElementById('root')).render(<RootApp />)
